import type { JSX, ReactNode } from 'react'
import './form-system.css'

/**
 * FormContainer — root wrapper for any form built with the KC3 form system.
 * Applies the `.kc3-form` scope (focus rings, accent colour, animations) and a
 * consistent vertical rhythm between the sections/cards it contains.
 */
export function FormContainer({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string | undefined
}): JSX.Element {
  return <div className={`kc3-form space-y-6 ${className}`}>{children}</div>
}

/**
 * FormCard — a plain surface card (soft shadow, subtle border, rounded corners).
 * Use it as a generic container when you don't need a section header.
 */
export function FormCard({
  children,
  className = '',
  padded = true,
}: {
  children: ReactNode
  className?: string | undefined
  padded?: boolean | undefined
}): JSX.Element {
  return (
    <div
      className={`bg-white rounded-[3px] border border-gray-200 shadow-sm ${padded ? 'p-6' : ''} ${className}`}
    >
      {children}
    </div>
  )
}

/**
 * FormDivider — a horizontal rule, optionally with a centered label, used to
 * separate groups of fields inside a section.
 */
export function FormDivider({
  label,
  className = '',
}: {
  label?: string | undefined
  className?: string | undefined
}): JSX.Element {
  if (!label) {
    return <hr className={`border-t border-gray-100 ${className}`} />
  }
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="h-px flex-1 bg-gray-100" />
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</span>
      <span className="h-px flex-1 bg-gray-100" />
    </div>
  )
}

/**
 * FormRow — a responsive flex row: stacked on mobile, side-by-side from `md`.
 * Each child stretches to fill the available width equally.
 */
export function FormRow({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string | undefined
}): JSX.Element {
  return (
    <div className={`flex flex-col md:flex-row gap-5 md:gap-6 [&>*]:flex-1 [&>*]:min-w-0 ${className}`}>
      {children}
    </div>
  )
}

const COLUMN_CLASSES: Record<1 | 2 | 3, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
}

/**
 * FormGrid — a responsive field grid (1–3 columns) with consistent gutters.
 * Collapses to a single column on small screens.
 */
export function FormGrid({
  children,
  columns = 2,
  className = '',
}: {
  children: ReactNode
  columns?: 1 | 2 | 3 | undefined
  className?: string | undefined
}): JSX.Element {
  return (
    <div className={`grid ${COLUMN_CLASSES[columns]} gap-x-6 gap-y-5 ${className}`}>{children}</div>
  )
}

/**
 * SectionDescription — muted helper paragraph, used under headings or on its own.
 */
export function SectionDescription({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string | undefined
}): JSX.Element {
  return <p className={`text-sm text-gray-500 ${className}`}>{children}</p>
}
